/**
 * Enhances the Shopify Subscriptions app widget:
 * show allocation prices, compare-at, and a Save badge.
 * Keep a single variant block visible as the selected variant changes.
 */

/**
 * @param {string | undefined | null} value
 * @returns {string}
 */
function normalizeVariantId(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const gid = raw.match(/ProductVariant\/(\d+)/i);
  return gid?.[1] || raw;
}

/**
 * @param {HTMLElement} container
 * @param {string} [hintId]
 * @returns {string}
 */
function getSelectedVariantId(container, hintId) {
  const hinted = normalizeVariantId(hintId);
  if (hinted) return hinted;

  const form = container.closest('form') || container.closest('product-form-component');
  const input = form?.querySelector('input[name="id"]');
  if (input instanceof HTMLInputElement && input.value) return normalizeVariantId(input.value);

  const checked = document.querySelector(
    'variant-picker input[data-variant-id]:checked, variant-picker option[data-variant-id]:checked'
  );
  if (checked instanceof HTMLElement) return normalizeVariantId(checked.dataset.variantId || '');

  try {
    return normalizeVariantId(new URLSearchParams(window.location.search).get('variant'));
  } catch {
    return '';
  }
}

/**
 * @param {HTMLElement} container
 * @param {string} [hintId]
 */
function syncVisibleVariantBlock(container, hintId) {
  const blocks = container.querySelectorAll('.shopify_subscriptions_app_block[data-variant-id]');
  if (!blocks.length) return;

  const variantId = getSelectedVariantId(container, hintId);
  /** @type {HTMLElement | null} */
  let visible = null;
  let switched = false;

  blocks.forEach((block) => {
    if (!(block instanceof HTMLElement)) return;
    const blockId = normalizeVariantId(block.dataset.variantId);
    const match = Boolean(variantId) && (blockId === variantId || block.dataset.variantId === variantId);
    const wasHidden = block.classList.contains('shopify_subscriptions_app_block--hidden');
    block.classList.toggle('shopify_subscriptions_app_block--hidden', !match);
    if (match) {
      visible = block;
      if (wasHidden) switched = true;
    }
  });

  if (!visible && !variantId) {
    const first = blocks.item(0);
    if (first instanceof HTMLElement) {
      first.classList.remove('shopify_subscriptions_app_block--hidden');
      visible = first;
    }
  }

  if (!(visible instanceof HTMLElement) || !switched) return;

  const checked = visible.querySelector('input[type="radio"]:checked');
  if (checked instanceof HTMLInputElement) {
    checked.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/**
 * @param {string} [hintId]
 */
function syncAllSubscriptionWidgets(hintId) {
  document.querySelectorAll('.shopify_subscriptions_app_container').forEach((node) => {
    if (node instanceof HTMLElement) syncVisibleVariantBlock(node, hintId);
  });
}

/**
 * @param {string | undefined} value
 * @returns {string}
 */
function formatWidgetPrice(value) {
  if (!value) return '';
  const formatted = value.replace(/\s*GBP\s*/i, '').trim();
  if (!formatted || /^£?0(\.00)?$/.test(formatted)) return '';
  return formatted;
}

/**
 * @param {HTMLInputElement | HTMLSelectElement} source
 * @returns {number | null}
 */
function getSavePercent(source) {
  const text =
    source instanceof HTMLSelectElement
      ? source.options.item(Math.max(source.selectedIndex, 0))?.textContent || ''
      : source.getAttribute('aria-label') || '';
  const match = text.match(/(\d+)\s*%/);
  if (match?.[1]) return Number.parseInt(match[1], 10);

  const price = source instanceof HTMLElement ? source.dataset.variantPrice : undefined;
  const compare = source instanceof HTMLElement ? source.dataset.variantCompareAtPrice : undefined;
  const current = parsePriceNumber(price);
  const original = parsePriceNumber(compare);
  if (current == null || original == null || original <= current) return null;
  return Math.round(((original - current) / original) * 100);
}

/**
 * @param {string | undefined} value
 * @returns {number | null}
 */
function parsePriceNumber(value) {
  if (!value) return null;
  const numeric = value.replace(/[^\d.]/g, '');
  if (!numeric) return null;
  const parsed = Number.parseFloat(numeric);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * @param {Element} priceEl
 * @param {string} price
 * @param {string} compare
 */
function renderWidgetPrice(priceEl, price, compare) {
  const current = formatWidgetPrice(price);
  const original = formatWidgetPrice(compare);
  const showCompare = Boolean(original && current && original !== current);

  priceEl.classList.remove('shopify_subscriptions_app_block--hidden');
  priceEl.removeAttribute('aria-hidden');
  priceEl.replaceChildren();

  if (showCompare) {
    const compareEl = document.createElement('s');
    compareEl.className = 'compare-at-price';
    compareEl.textContent = original;
    priceEl.append(compareEl);
  }

  if (current) {
    const currentEl = document.createElement('span');
    currentEl.className = 'in_widget_price__current';
    currentEl.textContent = current;
    priceEl.append(currentEl);
  }
}

/**
 * @param {HTMLElement} group
 */
function enhanceSubscriptionGroup(group) {
  const radio = group.querySelector('input[type="radio"]');
  const select = group.querySelector('select');
  const priceEl = group.querySelector('.in_widget_price');
  if (!(radio instanceof HTMLInputElement) || !(priceEl instanceof HTMLElement)) return;

  const selectedOption =
    select instanceof HTMLSelectElement && select.selectedIndex >= 0 ? select.options.item(select.selectedIndex) : null;
  const price = selectedOption?.dataset.variantPrice || radio.dataset.variantPrice || '';
  const compare = selectedOption?.dataset.variantCompareAtPrice || radio.dataset.variantCompareAtPrice || '';
  const signature = `${price}|${compare}`;

  if (priceEl.dataset.renderedPrice !== signature) {
    priceEl.dataset.renderedPrice = signature;
    renderWidgetPrice(priceEl, price, compare);
  }

  if (radio.dataset.radioType !== 'selling_plan_group') return;

  const label = radio.closest('label');
  if (!(label instanceof HTMLElement)) return;

  const percent = getSavePercent(select instanceof HTMLSelectElement ? select : radio);
  const badgeLabel = percent && percent > 0 ? `Save ${percent}%` : '';
  const badge = label.querySelector('.shopify-subscriptions-save-badge');

  if (badgeLabel) {
    if (badge instanceof HTMLElement) {
      if (badge.textContent !== badgeLabel) badge.textContent = badgeLabel;
    } else {
      const nextBadge = document.createElement('span');
      nextBadge.className = 'shopify-subscriptions-save-badge';
      nextBadge.textContent = badgeLabel;
      const name = label.querySelector('.group_name');
      if (name) name.after(nextBadge);
      else label.append(nextBadge);
    }
  } else if (badge) {
    badge.remove();
  }
}

/**
 * @param {HTMLElement} container
 */
function enhanceSubscriptionsWidget(container) {
  container.querySelectorAll('.subscription_group').forEach((group) => {
    if (group instanceof HTMLElement) enhanceSubscriptionGroup(group);
  });
}

function initSubscriptionsWidgets() {
  document.querySelectorAll('.shopify_subscriptions_app_container').forEach((node) => {
    if (!(node instanceof HTMLElement) || node.dataset.enhanced === 'true') return;

    node.dataset.enhanced = 'true';
    syncVisibleVariantBlock(node);
    enhanceSubscriptionsWidget(node);
    node.addEventListener('change', () => enhanceSubscriptionsWidget(node));

    // Run once after the app's deferred script hides in-widget prices.
    window.setTimeout(() => {
      syncVisibleVariantBlock(node);
      enhanceSubscriptionsWidget(node);
    }, 0);
  });
}

/**
 * @param {Event} event
 */
function onProductSelect(event) {
  const selectEvent = /** @type {any} */ (event);
  const run = (resourceId) => {
    syncAllSubscriptionWidgets(String(resourceId || ''));
  };

  if (selectEvent?.promise && typeof selectEvent.promise.then === 'function') {
    selectEvent.promise
      .then((result) => {
        const resourceId = result?.detail?.resource?.id || result?.variant?.id;
        run(resourceId);
      })
      .catch(() => {});
    return;
  }

  run(selectEvent?.detail?.resource?.id);
}

document.addEventListener(
  'change',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.closest('variant-picker')) return;
    const option = target instanceof HTMLSelectElement ? target.selectedOptions.item(0) : target;
    const variantId = option instanceof HTMLElement ? option.dataset.variantId : '';
    syncAllSubscriptionWidgets(variantId);
  },
  true
);

document.addEventListener('shopify:product:select', onProductSelect);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSubscriptionsWidgets, { once: true });
} else {
  initSubscriptionsWidgets();
}

document.addEventListener('shopify:section:load', initSubscriptionsWidgets);
